import { useEffect, useRef, useState } from 'react';

interface UseStreamingTextOptions {
    isEnabled?: boolean | undefined;
    onComplete?: (() => void) | undefined;
    speed?: number | undefined;
}

interface UseStreamingTextReturn {
    displayedText: string;
    isStreaming: boolean;
}

export const useStreamingText = (
    text: string,
    options: UseStreamingTextOptions = {},
): UseStreamingTextReturn => {
    const { isEnabled = true, onComplete, speed = 75 } = options;

    const [currentPosition, setCurrentPosition] = useState(0);
    const [isStreaming, setIsStreaming] = useState(false);
    const hasCompletedRef = useRef(false);
    const words = text.split(' ');

    useEffect(() => {
        if (!isEnabled) {
            setCurrentPosition(words.length);

            return undefined;
        }

        setCurrentPosition(0);
        setIsStreaming(true);
        hasCompletedRef.current = false;

        return undefined;
    }, [text, isEnabled, words.length]);

    useEffect(() => {
        if (!isEnabled || currentPosition >= words.length) {
            if (
                isStreaming &&
                currentPosition >= words.length &&
                !hasCompletedRef.current
            ) {
                setIsStreaming(false);
                hasCompletedRef.current = true;
                onComplete?.();
            }

            return undefined;
        }

        const timeoutId = setTimeout(() => {
            setCurrentPosition((prev) => prev + 1);
        }, speed);

        return () => clearTimeout(timeoutId);
    }, [
        currentPosition,
        words.length,
        speed,
        isEnabled,
        isStreaming,
        onComplete,
    ]);

    const displayedText = isEnabled
        ? words.slice(0, currentPosition).join(' ')
        : text;

    return { displayedText, isStreaming };
};
